import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileStack, Pencil, Plus, Trash2, X } from 'lucide-react';
import type { LegalTemplate, TemplateKind } from '@worki/shared';
import { api, RequestError } from '../lib/api.js';
import { cn } from '../lib/cn.js';
import { Button, Field, Input } from './ui.js';
import { AnchoredMenu } from './AnchoredMenu.js';
import { ConfirmDialog, Modal } from './Modal.js';
import { useToast } from './Toast.js';

/**
 * The key the server sent back, rather than "something went wrong".
 *
 * A refused write always has a reason and the API answers with the key for it —
 * a subscription that lapsed, a body past the length limit. Every handler in
 * this file used to drop that on the floor and print `errors.unknown`, so the
 * screen that knew exactly what was wrong said nothing anyone could act on:
 * a lapsed subscription reads as a glitch, and retrying is the obvious response
 * to a glitch. Field keys come first — they name the part that was refused.
 */
export function failureKey(error: unknown): string {
  if (!(error instanceof RequestError)) return 'errors.unknown';
  return Object.values(error.fields)[0] ?? error.code;
}

/**
 * QUO-003 — the saved-text picker that sits inside a long field.
 *
 * The templates are business records on the server rather than browser state,
 * so the terms a user wrote on their laptop are there on their phone too, and
 * a template needs a name: it is picked from a list by that name, and "the
 * first forty characters of the body" is not a name.
 *
 * QUO-022 — the same control serves the items field, which saves a whole set of
 * rows instead of a paragraph. The difference is entirely in `value` and
 * `onInsert`, which the caller serialises; `kind` only keeps the two lists apart
 * so the terms picker never offers a body that is really a table.
 */
export function TemplateField({
  value,
  onInsert,
  label,
  kind = 'service',
}: {
  value: string;
  onInsert: (text: string) => void;
  label: string;
  kind?: TemplateKind;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LegalTemplate | null>(null);
  /*
   * The one being corrected, or null when the dialog is saving a new one. The
   * body is edited here too: a template that can only be renamed still has to
   * be retyped into the field and re-saved to fix a word in it.
   */
  const [editing, setEditing] = useState<LegalTemplate | null>(null);
  const [body, setBody] = useState('');
  const ref = useRef<HTMLButtonElement>(null);

  const templates = useQuery({
    queryKey: ['legal-templates', kind],
    queryFn: () => api.get<{ items: LegalTemplate[] }>(`/quotes/templates?kind=${kind}`),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['legal-templates'] });

  const create = useMutation({
    mutationFn: (payload: { name: string; body: string }) => api.post('/quotes/templates', { ...payload, kind }),
    onSuccess: () => {
      void invalidate();
      setNaming(false);
      setName('');
      toast.success(t('common.saved'));
    },
    onError: (error: unknown) => setNameError(failureKey(error)),
  });

  const update = useMutation({
    mutationFn: (payload: { id: string; name: string; body: string }) =>
      api.put(`/quotes/templates/${payload.id}`, { name: payload.name, body: payload.body, kind }),
    onSuccess: () => {
      void invalidate();
      setNaming(false);
      setEditing(null);
      setName('');
      toast.success(t('common.saved'));
    },
    onError: (error: unknown) => setNameError(failureKey(error)),
  });

  const remove = useMutation({
    mutationFn: (template: LegalTemplate) => api.del(`/quotes/templates/${template.id}`),
    onSuccess: () => {
      void invalidate();
      setConfirmDelete(null);
      toast.success(t('common.deleted'));
    },
    onError: (error: unknown) => {
      setConfirmDelete(null);
      toast.error(t(failureKey(error)));
    },
  });

  const items = templates.data?.items ?? [];

  const submitName = () => {
    if (!name.trim()) {
      setNameError('validation.templateName.required');
      return;
    }
    if (editing && !body.trim()) {
      setNameError('validation.templateBody.required');
      return;
    }
    setNameError(null);
    if (editing) {
      update.mutate({ id: editing.id, name: name.trim(), body });
      return;
    }
    create.mutate({ name: name.trim(), body: value });
  };

  /** The dialog, opened on a template rather than on the field's own text. */
  const startEdit = (template: LegalTemplate) => {
    setOpen(false);
    setNameError(null);
    setEditing(template);
    setName(template.name);
    setBody(template.body);
    setNaming(true);
  };

  /** The dialog, opened to save what is in the field right now. */
  const startCreate = (initialName = '') => {
    setOpen(false);
    setNameError(null);
    setEditing(null);
    setName(initialName);
    setBody(value);
    setNaming(true);
  };

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        ref={ref}
        onClick={() => setOpen((current) => !current)}
        aria-label={label}
        title={label}
        className="h-9 w-9 flex items-center justify-center hover:text-primary transition-colors"
      >
        <FileStack className="h-4 w-4 text-muted-foreground" />
      </button>

      {/* Portalled: the editor scrolls inside `page-container`, whose
          `overflow` would cut the list off at the bottom of the screen. */}
      <AnchoredMenu open={open} onClose={() => setOpen(false)} anchorRef={ref} align="end" width={240}>
        <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground border-b border-border flex items-center justify-between">
          <span>
            {label} <span className="ltr-nums">({items.length})</span>
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t('common.close')}
            className="p-0.5 rounded hover:bg-muted"
          >
            <X className="h-3 w-3" />
          </button>
        </div>

        <div className="max-h-48 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-3 py-3 text-xs text-muted-foreground">{t('quotes.noTemplates')}</p>
          )}
          {items.map((template) => (
            <div key={template.id} className="flex items-center gap-1 border-b border-border/60 last:border-0">
              <button
                type="button"
                onClick={() => {
                  onInsert(template.body);
                  setOpen(false);
                }}
                {...(kind === 'items' ? {} : { title: template.body })}
                className="flex-1 min-w-0 text-start px-3 py-2 text-xs hover:bg-muted/60 transition-colors truncate"
              >
                {template.name}
              </button>
              <button
                type="button"
                onClick={() => startEdit(template)}
                aria-label={t('quotes.editTemplate', { name: template.name })}
                className="p-1.5 text-muted-foreground hover:bg-muted rounded-md"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(template)}
                aria-label={t('quotes.deleteTemplate', { name: template.name })}
                className="p-1.5 me-1 text-destructive hover:bg-destructive/10 rounded-md"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          disabled={!value.trim()}
          onClick={() => startCreate()}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-medium text-primary border-t border-border hover:bg-primary/5 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('quotes.saveAsTemplate')}
        </button>
      </AnchoredMenu>

      {/* A named record needs a name, and window.prompt is unstyled and
          untranslatable — so the app's own dialog asks for it. */}
      <Modal
        open={naming}
        title={t(editing ? 'quotes.editTemplateTitle' : 'quotes.saveAsTemplate')}
        onClose={() => setNaming(false)}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setNaming(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={create.isPending || update.isPending}
              onClick={submitName}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <Field
          label={t('quotes.templateName')}
          htmlFor="template-name"
          {...(nameError ? { error: t(nameError) } : {})}
        >
          <Input
            id="template-name"
            value={name}
            invalid={Boolean(nameError)}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitName();
              }
            }}
          />
        </Field>

        {/* Only when correcting one. Saving a new template takes the body from
            the field it is being saved out of, which is on screen behind this
            dialog — showing it again would be asking for it twice. */}
        {editing && kind !== 'items' && (
          <Field label={t('quotes.templateBody')} htmlFor="template-body">
            <textarea
              id="template-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className="w-full h-32 p-3 rounded-xl text-sm resize-none bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </Field>
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={t('common.delete')}
        message={t('quotes.deleteTemplateConfirm', { name: confirmDelete?.name ?? '' })}
        loading={remove.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete)}
      />
    </div>
  );
}

/**
 * "Save as template", beside the field it would save.
 *
 * Saving one used to be the last item inside the picker's own menu, which is a
 * place nobody opens while they are writing: the menu is for reading templates
 * back, and by the time somebody wants it they have already typed the sentence
 * they wish they had saved. So the offer is made where the sentence is, and
 * only once there is one — an empty field has nothing to save and a link that
 * is always there stops being read.
 *
 * The name is the opening of the text rather than a question. A template does
 * need a name, but asking for one turns a single press into a dialog, and the
 * first few words are what somebody would have typed anyway; the pencil in the
 * picker corrects it, along with the body.
 */
export function SaveTemplateLink({
  value,
  kind = 'service',
  className,
}: {
  value: string;
  kind?: TemplateKind;
  className?: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (payload: { name: string; body: string }) => api.post('/quotes/templates', { ...payload, kind }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legal-templates'] });
      toast.success(t('quotes.templateSaved'));
    },
    onError: (error: unknown) => toast.error(t(failureKey(error))),
  });

  const text = value.trim();
  if (!text) return null;

  return (
    <button
      type="button"
      disabled={save.isPending}
      onClick={() => save.mutate({ name: templateNameFrom(text), body: text })}
      className={cn(
        'inline-flex items-center gap-1 text-[11px] text-primary hover:underline disabled:opacity-50',
        className,
      )}
    >
      <Plus className="h-3 w-3" />
      {t('quotes.saveAsTemplate')}
    </button>
  );
}

/** The first line, trimmed to something that reads as a name in a short list. */
function templateNameFrom(text: string): string {
  const [first = ''] = text.split(/\r?\n/);
  const source = first.trim() || text.trim();
  return source.length <= 40 ? source : `${source.slice(0, 39).trimEnd()}…`;
}
